import { Project } from '@/mocks/projects';

interface Props {
  project: Project;
  onClick: (project: Project) => void;
}

const ProjectCard = ({ project, onClick }: Props) => {
  // Get up to 3 additional thumbnails (excluding main img)
  const thumbs = project.imgs
    .filter((img) => img !== project.img)
    .slice(0, 3);

  return (
    <div
      className="group bg-white border border-[#E2E8F0] rounded-lg overflow-hidden cursor-pointer hover:border-[#C88A2A]/30 hover:shadow-lg transition-all duration-300"
      onClick={() => onClick(project)}
    >
      {/* Main Image */}
      <div className="relative w-full h-40 sm:h-52 md:h-56 overflow-hidden bg-[#0A0A0A]">
        <img
          src={project.img}
          alt={`${project.title} - IngemetalSur`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            const t = e.currentTarget;
            t.style.display = 'none';
            const p = t.parentElement;
            if (p) {
              p.style.background = 'linear-gradient(135deg, #1A3A5C 0%, #0C1D2E 100%)';
              p.innerHTML = `<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px"><i class="ri-building-2-line" style="color:rgba(255,255,255,0.3);font-size:3rem"></i><span style="color:rgba(255,255,255,0.5);font-size:0.875rem;font-weight:500;text-align:center;padding:0 1rem">${project.title}</span></div>`;
            }
          }}
        />
        {/* Category badge */}
        <div className="absolute top-3 left-3 z-10">
          <span className="inline-block bg-[#C88A2A] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
            {project.category}
          </span>
        </div>
        {/* Year badge */}
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-[#0A0A0A]/70 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-full">
            {project.year}
          </span>
        </div>
      </div>

      {/* Thumbnails strip */}
      {thumbs.length > 0 && (
        <div className="flex gap-1 px-1 pt-1">
          {thumbs.map((url, i) => (
            <div key={i} className="flex-1 h-12 sm:h-14 rounded overflow-hidden bg-[#F8FAFC]">
              <img
                src={url}
                alt={`${project.title} foto ${i + 2}`}
                className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        {/* Title */}
        <h3 className="font-heading font-bold text-[#0C1D2E] text-base leading-tight mb-2 uppercase tracking-wide group-hover:text-[#1A3A5C] transition-colors">
          {project.title}
        </h3>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-1">
            <i className="ri-map-pin-line text-[#C88A2A] text-xs"></i>
            <span className="text-[#475569] text-xs font-medium">{project.location}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-calendar-line text-[#C88A2A] text-xs"></i>
            <span className="text-[#475569] text-xs font-medium">{project.year}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-ruler-line text-[#C88A2A] text-xs"></i>
            <span className="text-[#475569] text-xs font-medium">{project.area}</span>
          </div>
        </div>

        {/* Technical description */}
        <p className="text-[#475569] text-sm leading-relaxed line-clamp-3 mb-3">
          {project.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {project.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="bg-[#F1F5F9] text-[#64748B] text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-1.5 text-[#1A3A5C] text-xs font-bold uppercase tracking-wider group-hover:text-[#C88A2A] transition-colors">
          <i className="ri-eye-line text-sm"></i>
          <span>Ver detalle del proyecto</span>
          <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;